import React, { useContext } from 'react';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';

import SearchState from '../../elements/SearchState';
import LoadingArea from '@molecules/LoadingArea';
import { ViewTypeContext } from '@context/viewTypeContext';
import { useScreenSmDown } from '@hooks/useResponsive';

// members
import MemberListItem from '@molecules/MemberListItem';
import MemberCard from '@molecules/MemberCard';
import { mapSearchMemberToProps } from '@general/transforms/member.transform';

// companies
import CompanyListItem from '@molecules/CompanyListItem';
import CompanyCard from '@molecules/CompanyCard';
import { mapSearchCompanyToProps } from '@general/transforms/company.transform';

// articles
import ArticleListItem from '@molecules/ArticleListItem';
import ArticleCard from '@molecules/ArticleCard';
import { mapSearchArticleToProps } from '@general/transforms/article.transform';

// publications
import PublicationListItem from '@molecules/PublicationListItem';
import PublicationCard from '@molecules/PublicationCard';
import { mapSearchPublicationToProps } from '@general/transforms/publication.transform';

// events
import EventListItem from '@molecules/EventListItem';
import EventCard from '@molecules/EventCard';
import SiteContentListItem from '@molecules/SiteContentListItem';
import SiteContentCard from '@molecules/SiteContentCard';
import SessionListItem from '@molecules/SessionListItem';
import SessionCard from '@molecules/SessionCard';
import useFavoritesAll from '../../../hooks/useFavoritesAll';
import { mapSearchEventToProps } from '@general/transforms/event.transform';
import { mapSearchSiteContentToProps } from '@general/transforms/siteContent.transform';
import { mapSearchSessionToProps } from '@general/transforms/session.transform';

// maps to content_type from Algolia
const contentTypeOptions = {
    member: {
        listItemComponent: MemberListItem,
        cardComponent: MemberCard,
        transformFunc: mapSearchMemberToProps,
    },
    company: {
        listItemComponent: CompanyListItem,
        cardComponent: CompanyCard,
        transformFunc: mapSearchCompanyToProps,
    },
    article: {
        listItemComponent: ArticleListItem,
        cardComponent: ArticleCard,
        transformFunc: mapSearchArticleToProps,
    },
    publication: {
        listItemComponent: PublicationListItem,
        cardComponent: PublicationCard,
        transformFunc: mapSearchPublicationToProps,
    },
    event: {
        listItemComponent: EventListItem,
        cardComponent: EventCard,
        transformFunc: mapSearchEventToProps,
    },
    siteContent: {
        listItemComponent: SiteContentListItem,
        cardComponent: SiteContentCard,
        transformFunc: mapSearchSiteContentToProps,
    },
    session: {
        listItemComponent: SessionListItem,
        cardComponent: SessionCard,
        transformFunc: mapSearchSessionToProps,
    },
};

// const SearchItemStyled = styled.div`
//     @media (min-width: 1400px) {
//         flex: 0 0 33.33333%;
//         max-width: 33.33333%;
//     }
// `;

const SearchResultsEverything = ({ hits, onClick, isLoggedIn }) => {
    const { allFavoriteIds, isLoadingAll } = useFavoritesAll();

    const [viewType] = useContext(ViewTypeContext);

    const isScreenSmDown = useScreenSmDown();

    const getIsFavorite = (hitId, contentType) => {
        if (isLoadingAll || !allFavoriteIds[contentType]) return;
        const id = parseInt(hitId.split('-')[1], 10);
        return allFavoriteIds[contentType].includes(id);
    };

    const handleClick = ({ hit, url }) => {
        if (onClick) {
            onClick({ hit, url, index: 'everything' });
        }
    };

    const renderListItem = hit => {
        const contentTypeOption = contentTypeOptions[hit.content_type];
        const isFavorite = getIsFavorite(hit.objectID, hit.content_type);
        const ListItemComponent = contentTypeOption.listItemComponent;
        const { transformFunc } = contentTypeOption;
        return (
            <div key={hit.objectID}>
                <ListItemComponent
                    isLoggedIn={isLoggedIn}
                    {...transformFunc({
                        hit,
                        isFavorite,
                        onClick: url => handleClick({ hit, url }),
                    })}
                />
            </div>
        );
    };

    const renderCard = hit => {
        const contentTypeOption = contentTypeOptions[hit.content_type];
        const isFavorite = getIsFavorite(hit.objectID, hit.content_type);
        const CardComponent = contentTypeOption.cardComponent;
        const { transformFunc } = contentTypeOption;

        return (
            <div key={hit.objectID} className="col-md-6 col-xl-4 mb-2rem">
                <CardComponent
                    isLoggedIn={isLoggedIn}
                    {...transformFunc({
                        hit,
                        isFavorite,
                        onClick: url => handleClick({ hit, url }),
                    })}
                />
            </div>
        );
    };

    if (hits.length === 0) {
        return <SearchState hitCount={hits.length} />;
    }

    // blocking until favorites are loaded, don't love that, but the other
    // option is to 2-way binding on the useFavoritesContact hook
    if (isLoadingAll) {
        return <LoadingArea />;
    }

    if (isScreenSmDown) {
        return <div style={{ borderBottom: '1px solid #eceff1' }}>{hits.map(hit => renderListItem(hit))}</div>;
    }

    if (!isScreenSmDown && viewType === 'list') {
        return <div style={{ borderBottom: '1px solid #eceff1' }}>{hits.map(hit => renderListItem(hit))}</div>;
    }

    if (!isScreenSmDown && viewType === 'card') {
        return <div className="row">{hits.map(hit => renderCard(hit))}</div>;
    }

    return null;
};

SearchResultsEverything.propTypes = {
    hits: PropTypes.any,
    onClick: PropTypes.func,
    isLoggedIn: PropTypes.bool,
};

export default SearchResultsEverything;
